import { createContext, useContext, useEffect, useState } from "react";

// Fixed regional pricing (no live conversion). Annual is billed yearly.
export const PRICES = {
  usd: { symbol: "$", code: "USD", annualPerMonth: "3.99", annualTotal: "47.88", monthly: "6.99", reading: "9.99", chatPack: "2.99" },
  eur: { symbol: "€", code: "EUR", annualPerMonth: "4.50", annualTotal: "54.00", monthly: "5.35", reading: "9.00", chatPack: "2.79" },
  gbp: { symbol: "£", code: "GBP", annualPerMonth: "3.00", annualTotal: "36.00", monthly: "4.50", reading: "7.50", chatPack: "2.49" },
};

// European countries -> EUR. United Kingdom -> GBP. Everyone else -> USD.
const EUROPE = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
  "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES",
  "SE", "IS", "LI", "NO", "CH",
]);

function currencyForCountry(cc) {
  if (!cc) return "usd";
  const c = cc.toUpperCase();
  if (c === "GB") return "gbp";
  if (EUROPE.has(c)) return "eur";
  return "usd";
}

function localeFallback() {
  try {
    const region = (navigator.language || "").split("-")[1];
    return currencyForCountry(region);
  } catch {
    return "usd";
  }
}

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => localStorage.getItem("astra_currency") || null);

  useEffect(() => {
    if (currency) return;
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3500);
    (async () => {
      let detected = localeFallback();
      try {
        const res = await fetch("https://ipapi.co/json/", { signal: controller.signal });
        if (res.ok) {
          const data = await res.json();
          detected = currencyForCountry(data.country_code || data.country);
          if (data.country_code) localStorage.setItem("astra_country", data.country_code);
        }
      } catch {
        /* use locale fallback */
      }
      clearTimeout(timer);
      if (!cancelled) {
        localStorage.setItem("astra_currency", detected);
        setCurrency(detected);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [currency]);

  const active = currency || "usd";
  const changeCurrency = (c) => {
    if (!PRICES[c]) return;
    localStorage.setItem("astra_currency", c);
    setCurrency(c);
  };
  return (
    <CurrencyContext.Provider value={{ currency: active, prices: PRICES[active], changeCurrency, setCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
